import { openInNewTab } from "@/utils/open-in-new-tab";
import { logUserAction } from "@/apis/logs/route";
import type { DsdCompareHistoryItem } from "@/apis/dsd-dsd-compare/types";
import {
  PeriodChip,
  toReportPeriod,
  toReportType,
  TypeChip,
  YearChip,
} from "@/components/common/report-chip";
import { useDsdCompareHistory } from "@/hooks/queries/use-dsd-compare-history";
import { cn } from "@/lib/utils";
import { Avatar } from "@/toggle-ui-kit/avatar";
import { IconButton } from "@/toggle-ui-kit/icon-button";
import { History, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";

// ============================================
// 타입 정의
// ============================================

export interface HistorySidebarProps {
  /** 사이드바 열림 상태 */
  isOpen: boolean;
  /** 사이드바 닫기 핸들러 */
  onClose: () => void;
}

// ============================================
// 날짜 포맷 유틸리티
// ============================================

interface FormattedDate {
  label: string; // "오늘", "어제", "2025.01.15"
  time: string; // "13:44"
}

/**
 * UTC ISO 문자열을 KST 기준으로 변환하여 날짜 라벨과 시간을 반환
 * @param isoString - UTC ISO 8601 문자열 (예: "2026-02-05T14:30:00")
 */
const formatRelativeDate = (isoString: string): FormattedDate => {
  // UTC 문자열 명시적 처리 (Z가 없으면 추가)
  const utcString = isoString.endsWith("Z") ? isoString : `${isoString}Z`;
  const date = new Date(utcString);

  if (isNaN(date.getTime())) {
    return { label: "-", time: "-" };
  }

  // KST 기준으로 날짜/시간 추출
  const kstFormatter = new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = kstFormatter.formatToParts(date);
  const getPart = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? "";

  const year = getPart("year");
  const month = getPart("month");
  const day = getPart("day");
  const hours = getPart("hour");
  const minutes = getPart("minute");
  const time = `${hours}:${minutes}`;

  // 오늘/어제 판별 (KST 기준)
  const now = new Date();
  const todayKST = new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);

  const yesterdayDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const yesterdayKST = new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(yesterdayDate);

  const targetDateKST = `${year}. ${month}. ${day}.`;

  if (targetDateKST === todayKST) {
    return { label: "오늘", time };
  }
  if (targetDateKST === yesterdayKST) {
    return { label: "어제", time };
  }

  return { label: `${year}.${month}.${day}`, time };
};

// ============================================
// HistoryItem 컴포넌트
// ============================================

interface HistoryItemProps {
  item: DsdCompareHistoryItem;
  onClick?: () => void;
}

const HistoryItem = ({ item, onClick }: HistoryItemProps) => {
  const { label: dateLabel, time } = formatRelativeDate(item.requestedAt);

  const year = item.year;
  const reportPeriod = toReportPeriod(item.period);
  const reportType = toReportType(item.reportType);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative flex flex-col gap-4 px-4 py-5 w-full text-left overflow-hidden",
        "hover:bg-neutral-50 active:bg-neutral-100 transition-colors",
      )}
    >
      {/* 칩 행 — 모르는 값은 칩을 빼 추측값을 단언하지 않는다 */}
      <div className="flex items-center gap-1 w-full">
        {reportType && <TypeChip type={reportType} size="small" />}
        {year && <YearChip year={year} size="small" />}
        {reportPeriod && <PeriodChip period={reportPeriod} size="small" />}
      </div>

      {/* 파일 정보 + 메타 */}
      <div className="flex flex-col gap-3 w-full">
        <p className="label-2-medium text-label-normal truncate w-full">
          {item.sourceFile.filename}
        </p>
        <p className="label-2-medium text-label-normal truncate w-full">
          {item.targetFile.filename}
        </p>
        <div className="flex items-center gap-3">
          <span className="caption-1-medium text-label-alternative flex gap-1 shrink-0">
            <span>{dateLabel}</span>
            <span>{time}</span>
          </span>
          {item.uploaderName && (
            <div className="flex items-center gap-1 shrink-0">
              <Avatar value={item.uploaderName} size="16" />
              <span className="caption-1-medium text-label-alternative">
                {item.uploaderName}
              </span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

// ============================================
// EmptyState 컴포넌트
// ============================================

const EmptyState = () => {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2">
      <div className="flex items-center p-3 bg-neutral-100 rounded-lg">
        <History size={20} className="text-neutral-500" />
      </div>
      <p className="body-2-normal-medium text-label-normal">
        비교 내역이 아직 없습니다.
      </p>
    </div>
  );
};

// ============================================
// LoadingState 컴포넌트
// ============================================

const LoadingState = () => {
  return (
    <div className="flex items-center justify-center h-32 p-4">
      <Loader2 className="size-6 animate-spin text-neutral-400" />
    </div>
  );
};

// ============================================
// HistorySidebar 컴포넌트
// ============================================

export const HistorySidebar = ({ isOpen, onClose }: HistorySidebarProps) => {
  const { items, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useDsdCompareHistory();

  // 무한 스크롤을 위한 ref
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // 무한 스크롤 콜백
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  // IntersectionObserver 설정
  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    observerRef.current = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
    });

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  // 히스토리 아이템 클릭 시 새 탭에서 비교 페이지 열기
  const handleItemClick = (item: DsdCompareHistoryItem) => {
    logUserAction("DSD 비교내역 다시 열기");
    const url = `/compare/dsd-dsd/result?src=${item.sourceFile.fileId}&trg=${item.targetFile.fileId}`;
    openInNewTab(url);
  };

  return (
    <div
      className={cn(
        "h-full overflow-hidden",
        "transition-[width] duration-300 ease-in-out",
        isOpen ? "w-[360px]" : "w-0",
      )}
    >
      {/* 내부 콘텐츠 - 고정 너비로 리플로우 방지 */}
      <div className="w-[360px] h-full bg-white border-l border-line-normal flex flex-col py-6">
        {/* 헤더 */}
        <div className="flex items-center gap-2 border-b border-line-normal px-4 py-3 shrink-0">
          <h2 className="flex-1 body-2-normal-semibold text-label-normal">
            비교 내역
          </h2>
          <IconButton
            variant="transparent"
            icon={<X size={16} />}
            onClick={onClose}
            aria-label="닫기"
          />
        </div>

        {/* 컨텐츠 */}
        <div className="flex-1 flex flex-col overflow-y-auto">
          {isLoading ? (
            <LoadingState />
          ) : items.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="flex flex-col">
              {items.map((item) => (
                <HistoryItem
                  key={item.id}
                  item={item}
                  onClick={() => handleItemClick(item)}
                />
              ))}

              {/* 무한 스크롤 트리거 */}
              <div ref={loadMoreRef} className="h-4">
                {isFetchingNextPage && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="size-4 animate-spin text-neutral-400" />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

HistorySidebar.displayName = "HistorySidebar";

export default HistorySidebar;
